import { supabase } from "@/lib/supabase-client";

export interface SMSConversation {
  externalNumber: string;
  lastMessage: string;
  lastMessageDate: string;
  direction: "inbound" | "outbound";
  messageCount: number;
  unread: boolean;
}

export interface SMSMessage {
  sid: string;
  body: string;
  from: string;
  to: string;
  direction: "inbound" | "outbound";
  dateSent: string | null;
  dateCreated: string;
  status: string;
}

export interface SendMessageResult {
  messageSid: string;
  status: string;
  to: string;
  from: string;
}

async function getAccessToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }
  return session.access_token;
}

export const twilioSmsService = {
  async getConversations(twilioNumber: string): Promise<SMSConversation[]> {
    const { data, error } = await supabase
      .from("sms_conversation_summary")
      .select("external_number, last_message, last_message_date, last_direction, message_count, unread")
      .eq("twilio_number", twilioNumber)
      .order("last_message_date", { ascending: false });

    if (error) {
      throw new Error(error.message || "Failed to fetch conversations");
    }

    return (data ?? []).map((row) => ({
      externalNumber: row.external_number,
      lastMessage: row.last_message ?? "",
      lastMessageDate: row.last_message_date,
      direction: (row.last_direction ?? "outbound") as "inbound" | "outbound",
      messageCount: row.message_count ?? 0,
      unread: !!row.unread,
    }));
  },

  async getMessages(twilioNumber: string, externalNumber: string, limit?: number): Promise<SMSMessage[]> {
    const readFromDb = async () => {
      const { data, error } = await supabase
        .from("sms_messages")
        .select("message_sid, body, twilio_number, external_number, direction, status, twilio_created_at, created_at")
        .eq("twilio_number", twilioNumber)
        .eq("external_number", externalNumber)
        .order("twilio_created_at", { ascending: true, nullsFirst: true })
        .limit(limit || 200);
      if (error) throw new Error(error.message || "Failed to fetch messages");
      return (data ?? []).map((m) => ({
        sid: m.message_sid ?? `${m.twilio_number}-${m.external_number}-${m.created_at}`,
        body: m.body ?? "",
        from: m.direction === "inbound" ? m.external_number : m.twilio_number,
        to: m.direction === "inbound" ? m.twilio_number : m.external_number,
        direction: m.direction as "inbound" | "outbound",
        dateSent: null,
        dateCreated: m.twilio_created_at ?? m.created_at,
        status: m.status ?? "",
      }));
    };

    let rows = await readFromDb();
    if (rows.length === 0) {
      // Lazy backfill: ask the edge function to fetch + persist this thread once.
      const token = await getAccessToken();
      const { error } = await supabase.functions.invoke("twilio", {
        body: { action: "get_conversation", phoneNumber: twilioNumber, to: externalNumber, limit: limit || 100 },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (error) throw new Error(error.message || "Failed to fetch messages");
      rows = await readFromDb();
    }
    return rows;
  },

  async sendMessage(from: string, to: string, message: string): Promise<SendMessageResult> {
    const token = await getAccessToken();

    const { data, error } = await supabase.functions.invoke("twilio", {
      body: { action: "send_message", from, to, message },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (error) {
      throw new Error(error.message || "Failed to send message");
    }

    return data as SendMessageResult;
  },

  async getOwnedNumbers(): Promise<{ phoneNumber: string; friendlyName: string }[]> {
    const token = await getAccessToken();

    const { data, error } = await supabase.functions.invoke("twilio", {
      body: { action: "list_numbers" },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (error) {
      throw new Error(error.message || "Failed to fetch phone numbers");
    }

    return data?.numbers || [];
  },
};
